/**
 * FluxoGerencial Query Hooks
 *
 * Custom hooks for the fluxoGerencial router endpoints.
 * Uses direct Amplify calls (same pattern as progress-hooks.ts)
 * because fluxoGerencial types are not yet in the @ds/ds-api-types package.
 */

import { useQuery } from '@tanstack/react-query'
import { get } from 'aws-amplify/api'

// ============================================================================
// Types (mirror the backend Zod schemas)
// ============================================================================

interface FluxoGerencial {
  Id: number
  Data: string
  Valor: number
  CentroCusto: string
  Natureza: string | null
  Descricao: string | null
  NumeroNota: string | null
  Empresa: string | null
  Entrada: number | null
  Saida: number | null
  Ajuste: string | null
  Mes: string | null
  ImportacaoId: number
  CentroResultado: number | null
}

interface FluxoGerencialHome {
  Id: number
  CodigoCliente: string | null
  Cliente: string | null
  Data: string | null
  Valor: number | null
  Natureza: string | null
  Nota: string | null
  Empresa: string | null
  ImportacaoId: number | null
  CentroResultado: string | null
  Job: string | null
}

interface ListResponse {
  items: FluxoGerencial[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

interface ReportResponse {
  items: FluxoGerencialHome[]
  total: number
}

interface ListReportResponse {
  items: FluxoGerencialHome[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

export interface ListReportFilters {
  dataInicial: Date
  dataFinal: Date
  codigoCliente?: string
  natureza?: string
  centroResultado?: string
  empresa?: string
  centroCusto?: string
  search?: string
  valorMin?: number
  valorMax?: number
  page?: number
  pageSize?: number
  sortBy?: 'Data' | 'Valor'
  sortDir?: 'asc' | 'desc'
}

export type FluxoGerencialHomeRow = FluxoGerencialHome

interface FluxoGerencialClient {
  codigo: string
  nome: string
}

// ============================================================================
// Amplify fetch helper
// ============================================================================

async function fluxoGet<T>(path: string, params?: Record<string, string>): Promise<T> {
  const queryString = params
    ? '?' + new URLSearchParams(params).toString()
    : ''

  const fullPath = `/fluxoGerencial${path}${queryString}`

  const restOperation = get({
    apiName: 'monumenta-trpc',
    path: fullPath,
  })

  const response = await restOperation.response
  const body = await response.body.text()
  const parsed = JSON.parse(body)

  if (parsed.result?.data) return parsed.result.data
  if (parsed.error) throw new Error(parsed.error.message || 'Unknown error')
  return parsed
}

function formatDateParam(date: Date): string {
  return date.toISOString()
}

// ============================================================================
// Hooks
// ============================================================================

export function useFluxoGerencialList(params: {
  dataInicial: Date
  dataFinal: Date
  page: number
  pageSize?: number
}) {
  return useQuery<ListResponse>({
    queryKey: ['fluxoGerencial', 'list', params],
    queryFn: () => fluxoGet('/list', {
      input: JSON.stringify({
        dataInicial: formatDateParam(params.dataInicial),
        dataFinal: formatDateParam(params.dataFinal),
        page: params.page,
        pageSize: params.pageSize ?? 30,
      }),
    }),
  })
}

export function useFluxoGerencialById(id: number) {
  return useQuery<FluxoGerencial>({
    queryKey: ['fluxoGerencial', 'getById', id],
    queryFn: () => fluxoGet('/getById', {
      input: JSON.stringify({ id }),
    }),
    enabled: id > 0,
  })
}

export function useFluxoGerencialReport(params: {
  dataInicial: Date
  dataFinal: Date
  codigoCliente?: string
  enabled?: boolean
}) {
  const { enabled = true, ...rest } = params
  return useQuery<ReportResponse>({
    queryKey: ['fluxoGerencial', 'getReport', rest],
    queryFn: () => fluxoGet('/getReport', {
      input: JSON.stringify({
        dataInicial: formatDateParam(rest.dataInicial),
        dataFinal: formatDateParam(rest.dataFinal),
        ...(rest.codigoCliente ? { codigoCliente: rest.codigoCliente } : {}),
      }),
    }),
    enabled,
  })
}

export function useFluxoGerencialClients(params: {
  dataInicial: Date
  dataFinal: Date
}) {
  return useQuery<FluxoGerencialClient[]>({
    queryKey: ['fluxoGerencial', 'getClients', params],
    queryFn: () => fluxoGet('/getClients', {
      input: JSON.stringify({
        dataInicial: formatDateParam(params.dataInicial),
        dataFinal: formatDateParam(params.dataFinal),
      }),
    }),
  })
}

/**
 * Paginated report query — primary data source for the unified table on the
 * redesigned page. Returns rows from vwFluxoGerencialHome with the JOINed
 * Cliente/Job/Empresa columns, paginated server-side.
 */
export function useFluxoGerencialListReport(filters: ListReportFilters) {
  const page = filters.page ?? 1
  const pageSize = filters.pageSize ?? 30
  const sortBy = filters.sortBy ?? 'Data'
  const sortDir = filters.sortDir ?? 'desc'

  const input: Record<string, unknown> = {
    dataInicial: formatDateParam(filters.dataInicial),
    dataFinal: formatDateParam(filters.dataFinal),
    page,
    pageSize,
    sortBy,
    sortDir,
  }
  if (filters.codigoCliente) input.codigoCliente = filters.codigoCliente
  if (filters.natureza) input.natureza = filters.natureza
  if (filters.centroResultado) input.centroResultado = filters.centroResultado
  if (filters.empresa) input.empresa = filters.empresa
  if (filters.centroCusto) input.centroCusto = filters.centroCusto
  if (filters.search) input.search = filters.search
  if (filters.valorMin !== undefined) input.valorMin = filters.valorMin
  if (filters.valorMax !== undefined) input.valorMax = filters.valorMax

  return useQuery<ListReportResponse>({
    queryKey: ['fluxoGerencial', 'listReport', input],
    queryFn: () => fluxoGet('/listReport', { input: JSON.stringify(input) }),
  })
}
